import { useEffect, useRef } from 'react';

/**
 * AmbientCanvas — the living "Humanity Mirror" entity.
 *
 * Visual language is a React port of the standalone Claude prototype
 * (canvas-engine.js) — mycelium-like radial filaments growing from a pulsing
 * core, wave field around the entity, code rain that bends toward the pointer,
 * cursor wave rings, click ripples, burst glyphs. Production wiring is kept:
 *   - voiceState  → smoothly eased voiceIntensity that drives every parameter
 *   - stage       → smoothly eased stageIntensity → awakeness
 *   - pointer / ripple / burst state lives in refs (no React re-render per frame)
 *   - DPR capped at 2, rAF + resize listeners cleaned up on unmount
 *
 * Props:
 *   stage       — 'dormant' | 'awakened' | 'voice_ready' | 'voice_active' | 'voice_ended' | 'loading' | 'result'
 *   voiceState  — 'sleeping' | 'waking' | 'listening' | 'speaking' | 'muted' | 'ended' | 'error' | 'generating'
 */

const VOICE_TARGETS = {
  sleeping: 0,
  waking: 0.4,
  listening: 0.55,
  speaking: 1.0,
  muted: 0.2,
  ended: 0.8,
  error: 0.1,
  generating: 1.2,
};

const STAGE_TARGETS = {
  dormant: 0,
  awakened: 0.55,
  voice_ready: 0.6,
  voice_active: 0.8,
  voice_ended: 0.7,
  loading: 0.95,
  result: 0.5,
  gallery: 0.45,
};

const GLYPHS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノ01∴∷⌁≋∽◯◌·.+-/<>{}[]';

export default function AmbientCanvas({ stage = 'dormant', voiceState = 'sleeping', mode = 'entity' }) {
  const canvasRef = useRef(null);
  // Refs are pulled from each invocation, so closures inside the rAF loop
  // see the current mode without remounting the canvas.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // refs only — never trigger a React re-render from the rAF loop
  const pointerRef = useRef({
    x: 0.5, y: 0.5, vx: 0, vy: 0, lastMove: 0, present: false, near: 0,
    dwellMs: 0,
  });
  const ripplesRef = useRef([]);
  const pulsesRef = useRef([]);
  const burstGlyphsRef = useRef([]);
  const anchorsRef = useRef([]);
  const anchorCooldownRef = useRef(0);
  const hyphaeRef = useRef([]);
  const columnsRef = useRef([]);
  const stageIntensityRef = useRef({ target: 0, current: 0 });
  const voiceIntensityRef = useRef({ target: 0, current: 0 });
  // 0..1 — accumulates while pointer is inside the central core radius, decays
  // smoothly when it leaves. Drives infinite outward filament growth without
  // attaching to a single anchor point.
  const coreDwellRef = useRef(0);
  const tRef = useRef(0);
  const rafRef = useRef(0);
  const sizeRef = useRef({ w: 0, h: 0, cx: 0, cy: 0 });

  // Sync stage/voice props into refs without re-creating the canvas effect
  useEffect(() => { stageIntensityRef.current.target = STAGE_TARGETS[stage] ?? 0; }, [stage]);
  useEffect(() => { voiceIntensityRef.current.target = VOICE_TARGETS[voiceState] ?? 0; }, [voiceState]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // ────────── sizing ──────────
    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { w, h, cx: w / 2, cy: h / 2 };
      initCodeRain();
    };

    // ────────── code rain ──────────
    function initCodeRain() {
      const { w, h } = sizeRef.current;
      const colW = 16;
      const cols = Math.ceil(w / colW);
      const arr = new Array(cols);
      for (let i = 0; i < cols; i += 1) {
        arr[i] = {
          x: i * colW + colW * 0.5,
          y: Math.random() * h,
          speed: 14 + Math.random() * 28,
          len: 6 + ((Math.random() * 18) | 0),
          glyphs: Array.from({ length: 24 }, () => GLYPHS[(Math.random() * GLYPHS.length) | 0]),
          jitter: Math.random() * 1000,
          bright: Math.random() < 0.06,
          flash: 0,
        };
      }
      columnsRef.current = arr;
    }

    // ────────── hyphae (mycelium) — built once, sorted inside-out ──────────
    function initHyphae() {
      const arr = [];
      const N = 220;
      for (let i = 0; i < N; i += 1) {
        const baseAng = (i / N) * Math.PI * 2 + (Math.random() - 0.5) * 0.25;
        const segs = 14 + ((Math.random() * 16) | 0);
        const stepLen = 4 + Math.random() * 3;
        const path = [];
        let x = 0;
        let y = 0;
        let a = baseAng;
        path.push({ x, y });
        for (let s = 0; s < segs; s += 1) {
          a += (Math.random() - 0.5) * 0.45;
          x += Math.cos(a) * stepLen;
          y += Math.sin(a) * stepLen * 1.05;
          path.push({ x, y });
        }
        arr.push({
          path,
          baseAng,
          seed: Math.random() * 999,
          thickness: 0.5 + Math.random() * 0.5,
          bright: Math.random() < 0.18,
          reachMul: 0.55 + (Math.random() ** 1.6) * 1.6,
          breathPhase: Math.random() * Math.PI * 2,
          breathRate: 0.25 + Math.random() * 0.35,
          reach: Math.hypot(path[path.length - 1].x, path[path.length - 1].y),
        });
      }
      arr.sort((a, b) => a.reach - b.reach);
      hyphaeRef.current = arr;
    }
    initHyphae();

    // ────────── pointer / touch ──────────
    const updatePointer = (clientX, clientY) => {
      const { w, h, cx, cy } = sizeRef.current;
      const nx = clientX / w;
      const ny = clientY / h;
      const ptr = pointerRef.current;
      const dx = nx - ptr.x;
      const dy = ny - ptr.y;
      ptr.vx = ptr.vx * 0.8 + dx * 0.2;
      ptr.vy = ptr.vy * 0.8 + dy * 0.2;
      ptr.x = nx;
      ptr.y = ny;
      ptr.lastMove = performance.now();
      ptr.present = true;
      ptr.near = Math.max(0, 1 - Math.hypot(clientX - cx, clientY - cy) / 360);
    };

    const onMove = (e) => updatePointer(e.clientX, e.clientY);
    const onTouchMove = (e) => { if (e.touches[0]) updatePointer(e.touches[0].clientX, e.touches[0].clientY); };
    const onLeave = () => { pointerRef.current.present = false; };

    const addRipple = (x, y, strength) => {
      const arr = ripplesRef.current;
      arr.push({ x, y, r: 0, life: 0, strength });
      if (arr.length > 14) arr.shift();
    };
    const triggerClickPulse = (cx, cy) => {
      const { cx: ECX, cy: ECY } = sizeRef.current;
      const ang = Math.atan2(cy - ECY, (cx - ECX) || 0.001);
      pulsesRef.current.push({ ang, t0: tRef.current, life: 1 });
      if (pulsesRef.current.length > 3) pulsesRef.current.shift();
      // Touch, not explosion: 4 quiet glyphs at low velocity.
      const n = 4;
      const bg = burstGlyphsRef.current;
      for (let i = 0; i < n; i += 1) {
        const a = (i / n) * Math.PI * 2 + Math.random() * 0.3;
        const speed = 22 + Math.random() * 38;
        bg.push({
          x: cx,
          y: cy,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          glyph: GLYPHS[(Math.random() * GLYPHS.length) | 0],
          born: tRef.current,
        });
      }
      if (bg.length > 24) bg.splice(0, bg.length - 24);
    };

    const onDown = (e) => {
      const x = e.clientX;
      const y = e.clientY;
      addRipple(x, y, 0.32);
      triggerClickPulse(x, y);
    };
    const onTouchStart = (e) => {
      if (!e.touches[0]) return;
      const t = e.touches[0];
      addRipple(t.clientX, t.clientY, 0.42);
      triggerClickPulse(t.clientX, t.clientY);
    };

    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('mouseleave', onLeave, { passive: true });
    window.addEventListener('mousedown', onDown, { passive: true });

    resize();

    // ────────── draw helpers ──────────
    function drawCodeRain(dt) {
      const { w, h, cx, cy } = sizeRef.current;
      const cols = columnsRef.current;
      const ptr = pointerRef.current;
      const vi = voiceIntensityRef.current.current;
      const aw = stageIntensityRef.current.current;
      const reactivity = aw * 0.6 + vi * 0.7;
      // Occasional column flashes — "connections happening"
      if (aw > 0.15 && Math.random() < 0.12 + aw * 0.18) {
        const k = (Math.random() * cols.length) | 0;
        const c = cols[k];
        if (c) c.flash = 1;
      }
      ctx.font = '11px "JetBrains Mono", "IBM Plex Mono", monospace';
      ctx.textBaseline = 'top';
      const px = ptr.x * w;
      const py = ptr.y * h;
      for (let i = 0; i < cols.length; i += 1) {
        const c = cols[i];
        if (c.flash > 0) c.flash = Math.max(0, c.flash - dt * 0.9);
        const dx = c.x - cx;
        const dy = (c.y % h) - cy;
        const d = Math.hypot(dx, dy);
        const halo = Math.max(0, 1 - d / 320);
        const pdx = c.x - px;
        c.y += (c.speed + reactivity * 30) * dt;
        if (c.y - c.len * 14 > h) c.y = -Math.random() * 200;
        const head = c.y;
        for (let k = 0; k < c.len; k += 1) {
          const yy = head - k * 14;
          if (yy < -14 || yy > h) continue;
          const tNorm = 1 - k / c.len;
          let alpha = tNorm * (0.10 + reactivity * 0.18);
          alpha *= 1 - halo * 0.85; // negative-space halo near entity
          if (alpha <= 0.002) continue;
          const gpdy = yy - py;
          const gpd = Math.hypot(pdx, gpdy);
          const localPull = Math.max(0, 1 - gpd / 240);
          const waveRing = Math.exp(-(((gpd - 120) / 70) ** 2)) * 0.9
            + Math.exp(-(((gpd - 220) / 90) ** 2)) * 0.5;
          const wavePhase = Math.sin(gpd * 0.04 - tRef.current * 3.2);
          const ang = Math.atan2(gpdy, pdx || 0.001);
          const xOff = Math.cos(ang) * waveRing * 14 * wavePhase
            - (pdx / Math.max(40, gpd)) * localPull * 22 * (1 - k / c.len);
          alpha *= 1 + waveRing * 0.7 + localPull * 0.4;
          const flashBoost = c.flash * (1 - k / c.len);
          alpha *= 1 + flashBoost * 1.8;
          if (c.flash > 0.1 && k <= 3) {
            ctx.fillStyle = `rgba(180,224,232,${Math.min(0.95, alpha + 0.3 + c.flash * 0.4)})`;
          } else if (k === 0 && c.bright) {
            ctx.fillStyle = `rgba(220,240,245,${Math.min(0.9, alpha + 0.45)})`;
          } else if (k === 0) {
            ctx.fillStyle = `rgba(237,234,227,${Math.min(0.9, alpha + 0.25)})`;
          } else {
            ctx.fillStyle = `rgba(237,234,227,${alpha})`;
          }
          const gi = c.glyphs[(k + ((tRef.current * 4 + c.jitter) | 0)) % c.glyphs.length];
          ctx.fillText(gi, c.x + xOff, yy);
        }
      }
    }

    function drawCursorWave() {
      const { w, h, cx, cy } = sizeRef.current;
      const ptr = pointerRef.current;
      if (!ptr.present) return;
      const px = ptr.x * w;
      const py = ptr.y * h;
      const speed = Math.hypot(ptr.vx, ptr.vy);
      const sinceMove = (performance.now() - ptr.lastMove) / 1000;
      const motionEnergy = Math.max(0, 1 - sinceMove / 1.6);
      const energy = 0.35 + motionEnergy * 0.65 + Math.min(1, speed * 18) * 0.3;
      const coreDwell = coreDwellRef.current;
      // When pointer is inside the entity's core, drop the cursor-centered
      // wave (the rings should not orbit the cursor) and instead emit a
      // wider concentric wavefront from the entity centre — the entity
      // opening from the inside.
      const useCoreOrigin = coreDwell > 0.05;
      const ox = useCoreOrigin ? cx : px;
      const oy = useCoreOrigin ? cy : py;
      const ringFadeStrength = useCoreOrigin ? coreDwell : 1;
      const rings = 4;
      const t = tRef.current;
      // Outward growth pushes the ring radii further as the visitor stays in
      // the core. Capped so it doesn't run off-screen.
      const outwardBoost = coreDwell * 240;
      for (let r = 0; r < rings; r += 1) {
        const phase = (t * 0.55 + r * 0.6) % 1.4;
        const radius = 40 + phase * 130 + outwardBoost * phase;
        const segs = 96;
        ctx.beginPath();
        for (let s = 0; s <= segs; s += 1) {
          const a = (s / segs) * Math.PI * 2;
          const n = Math.sin(a * 3 + t * 1.4 + r) * 3.5
            + Math.sin(a * 5 - t * 0.9) * 2.2;
          const rr = radius + n * energy;
          const x = ox + Math.cos(a) * rr;
          const y = oy + Math.sin(a) * rr;
          if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        const ringFade = 1 - phase / 1.4;
        const a = ringFade * 0.18 * energy * ringFadeStrength;
        if (a < 0.005) continue;
        ctx.strokeStyle = r === 0
          ? `rgba(180,224,232,${a * 1.2})`
          : `rgba(237,234,227,${a})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      void w; void h;
    }

    function updateAnchors(dt) {
      const { w, h, cx, cy } = sizeRef.current;
      const now = performance.now();
      const ptr = pointerRef.current;
      const px = ptr.x * w;
      const py = ptr.y * h;
      const dEntity = Math.hypot(px - cx, py - cy);
      const reachRadius = 360;
      const inReach = dEntity < reachRadius && ptr.present && stageIntensityRef.current.current > 0.4;
      const speed = Math.hypot(ptr.vx, ptr.vy);
      const sinceMove = (now - ptr.lastMove) / 1000;
      if (inReach && speed < 0.0015 && sinceMove > 0.25) {
        ptr.dwellMs += dt * 1000;
      } else {
        ptr.dwellMs *= 0.85;
      }
      anchorCooldownRef.current = Math.max(0, anchorCooldownRef.current - dt);
      const anchors = anchorsRef.current;
      if (ptr.dwellMs > 450 && anchorCooldownRef.current <= 0) {
        const tooClose = anchors.some((a) => Math.hypot(a.x - px, a.y - py) < 60);
        if (!tooClose && anchors.length < 4) {
          anchors.push({ x: px, y: py, birth: tRef.current, life: 0.001, target: 1 });
          anchorCooldownRef.current = 0.6;
          ptr.dwellMs = 0;
        }
      }
      for (let i = anchors.length - 1; i >= 0; i -= 1) {
        const a = anchors[i];
        const dPtr = Math.hypot(px - a.x, py - a.y);
        if (dPtr < 90 && ptr.present) a.target = 1;
        else if (dPtr > reachRadius + 80 || !ptr.present) a.target = 0;
        else a.target = Math.max(0, 1 - (dPtr - 90) / 220);
        a.life += (a.target - a.life) * Math.min(1, dt * 2.2);
        if (a.target === 0 && a.life < 0.04) anchors.splice(i, 1);
      }
      // unused but harmless: keep h/cy referenced
      void h; void cy;
    }

    function drawEntity() {
      const { w, h, cx, cy } = sizeRef.current;
      const aw = stageIntensityRef.current.current;
      const vi = voiceIntensityRef.current.current;
      const t = tRef.current;
      const breath = Math.sin(t * 0.7) * 0.5 + 0.5;
      const ptr = pointerRef.current;
      const px = ptr.x * w;
      const py = ptr.y * h;
      const pmag = Math.hypot(px - cx, py - cy);
      const pull = Math.max(0, 1 - pmag / 380) * (0.4 + aw * 0.5);
      const coreDwell = coreDwellRef.current;

      // Immediate proximity factor — only when OUTSIDE the core. When the
      // pointer enters the core we switch off the single-point pull and let
      // the core-dwell outward growth take over.
      const outsideCoreFactor = Math.max(0, 1 - coreDwell);
      const proximity = Math.max(0, 1 - pmag / 420) * (ptr.present ? 1 : 0) * outsideCoreFactor;
      const ptrAngle = Math.atan2(py - cy, (px - cx) || 0.001);

      // Voice-driven smooth pulse — slower, gentler than before. Speaking
      // feels like inhale/exhale through the organism, not a waveform.
      const speakingWeight = Math.max(0, Math.min(1, (vi - 0.7) / 0.3)) * Math.max(0, 1 - Math.max(0, vi - 1));
      const speakingPulse = Math.sin(t * 2.6) * 0.5 + 0.5; // ~0.4 Hz inhale/exhale
      const speakingShimmer = Math.sin(t * 6.5 + 1.7) * 0.5 + 0.5; // delicate undertone
      const listeningWeight = Math.max(0, Math.min(1, vi / 0.6)) - speakingWeight;
      const listeningSwell = Math.sin(t * 0.95) * 0.5 + 0.5;

      // central core + halo (voice-modulated, gentler)
      const corePulse = 0.5 + 0.5 * Math.sin(t * 1.1 + vi * 0.5);
      const dormantCore = 1 - aw;
      const voiceCoreBoost = speakingWeight * (0.22 + speakingPulse * 0.34 + speakingShimmer * 0.05)
        + listeningWeight * (0.16 + listeningSwell * 0.14)
        + proximity * 0.28
        + coreDwell * 0.35;
      const coreR = 6 + corePulse * 4 + aw * 5 + vi * 4 + voiceCoreBoost * 3;
      const haloR = 26 + corePulse * 10 + dormantCore * 8 + vi * 18 + voiceCoreBoost * 14 + coreDwell * 22;
      const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, haloR);
      // White stays dominant; cyan blends in delicately only while speaking.
      const cyanAccent = speakingWeight * 0.18 + coreDwell * 0.10;
      halo.addColorStop(0, `rgba(245,250,252,${0.35 + corePulse * 0.25 + vi * 0.16 + voiceCoreBoost * 0.16})`);
      halo.addColorStop(0.4, `rgba(${190 - cyanAccent * 35},${220 - cyanAccent * 8},${228 + cyanAccent * 22},${0.10 + corePulse * 0.10 + vi * 0.10 + voiceCoreBoost * 0.10 + cyanAccent * 0.18})`);
      halo.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, cy, haloR, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(245,250,252,${0.7 + corePulse * 0.3})`;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR * 0.45, 0, Math.PI * 2);
      ctx.fill();

      if (aw < 0.01) return;

      // hyphae — mycelium-like growing filaments
      const hyphae = hyphaeRef.current;
      const anchors = anchorsRef.current;
      const pulses = pulsesRef.current;
      const maxReach = 220;
      const pulse = Math.sin(t * 0.35) * 0.5 + 0.5;
      const pulse2 = Math.sin(t * 0.18 + 1.3) * 0.5 + 0.5;
      const generatingShrink = Math.max(0, vi - 1) * 0.35;
      // Visible voice "breath" expanding/contracting the mycelium when the
      // entity speaks. Softer amplitudes than the waveform version.
      const voiceBreath = speakingWeight * (0.10 + (speakingPulse - 0.5) * 0.18)
        + listeningWeight * (listeningSwell - 0.5) * 0.08;
      // Core dwell pushes filaments to grow much further outward —
      // "infinite" relative to the original radius; capped so they don't
      // exceed half a screen.
      const coreExpand = coreDwell * 0.85;
      const expansion = (aw * (0.78 + pulse * 0.32 + pulse2 * 0.18) + vi * 0.18 + voiceBreath + coreExpand)
        * (1 - generatingShrink);
      const reachMulBoost = 1 + coreDwell * 1.1; // longer filament radii while inside core
      for (let i = 0; i < hyphae.length; i += 1) {
        const filament = hyphae[i];
        // Subtle high-frequency vibration while speaking — much softer than
        // before so it never reads as mechanical.
        const speakWobble = speakingWeight * Math.sin(t * 9 + filament.seed) * 0.05;
        const filBreath = 0.5 + 0.5 * Math.sin(t * filament.breathRate + filament.breathPhase);
        const filExpand = 0.7 + filBreath * 0.55 + speakWobble;
        const reachNorm = filament.reach / maxReach;
        const grown = Math.max(0, Math.min(1, expansion * 1.7 * filament.reachMul * reachMulBoost * filExpand - reachNorm * 0.85));
        if (grown < 0.02) continue;
        const visSegs = Math.floor(grown * (filament.path.length - 1));
        if (visSegs < 1) continue;
        const last = filament.path[filament.path.length - 1];
        const ang0 = Math.atan2(last.y, last.x || 0.001);
        const reach = pull * 6 * (0.4 + reachNorm) * outsideCoreFactor;
        const scale = (1 + breath * 0.04 + aw * 0.05) * (0.85 + filBreath * 0.3) * filament.reachMul * (1 + coreDwell * 0.7);
        // anchor pull (suppressed inside core)
        let anchorPullX = 0;
        let anchorPullY = 0;
        let anchorWeight = 0;
        if (outsideCoreFactor > 0.05) {
          for (let aIdx = 0; aIdx < anchors.length; aIdx += 1) {
            const an = anchors[aIdx];
            if (an.life < 0.05) continue;
            const adx = an.x - cx;
            const ady = an.y - cy;
            const aang = Math.atan2(ady, adx || 0.001);
            const align = 0.5 + 0.5 * Math.cos(ang0 - aang);
            const wgt = (align ** 6) * an.life * outsideCoreFactor;
            if (wgt > anchorWeight) { anchorWeight = wgt; anchorPullX = adx; anchorPullY = ady; }
          }
        }
        // Immediate pointer pull — also suppressed when inside the core, so
        // filaments don't snap to the cursor when it's on the entity itself.
        const ptrAlign = 0.5 + 0.5 * Math.cos(ang0 - ptrAngle);
        const ptrBendWeight = (ptrAlign ** 4) * proximity;
        const ptrPullX = (px - cx) * ptrBendWeight;
        const ptrPullY = (py - cy) * ptrBendWeight;
        const combinedPullX = anchorPullX + ptrPullX * 0.6;
        const combinedPullY = anchorPullY + ptrPullY * 0.6;
        const combinedBendWeight = Math.max(anchorWeight, ptrBendWeight * 0.85);
        ctx.beginPath();
        let endX = cx;
        let endY = cy;
        const samples = [];
        for (let s = 0; s <= visSegs; s += 1) {
          const p = filament.path[s];
          const tt = s / (filament.path.length - 1);
          const sway = Math.sin(t * 0.5 + filament.seed + tt * 3) * 1.2 * tt;
          const swayX = -Math.sin(ang0) * sway;
          const swayY = Math.cos(ang0) * sway;
          const reachX = Math.cos(ang0) * reach * tt;
          const reachY = Math.sin(ang0) * reach * tt;
          const baseX = cx + p.x * scale;
          const baseY = cy + p.y * scale;
          const targetX = cx + combinedPullX;
          const targetY = cy + combinedPullY;
          const bend = combinedBendWeight * tt * tt;
          const x = baseX + (targetX - baseX) * bend * 0.55 + swayX + reachX;
          const y = baseY + (targetY - baseY) * bend * 0.55 + swayY + reachY;
          if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          endX = x; endY = y;
          samples.push({ x, y });
        }
        const tipFade = grown;
        const connecting = combinedBendWeight > 0.4;
        // Tips brighten very slightly during core-dwell to read as the
        // organism opening outward.
        const tipBrighten = coreDwell * (grown > 0.85 ? 0.45 : 0.1);
        const baseA = (filament.bright ? 0.32 : 0.16) * tipFade * (0.6 + aw * 0.5)
          * (1 + combinedBendWeight * 0.6 + tipBrighten);
        // Delicate cyan accent on filaments only while speaking (and softly
        // during core-dwell). Otherwise the warmer ivory tone stays dominant.
        const cyanBlend = speakingWeight * 0.55 + coreDwell * 0.25 + (connecting ? 0.7 : 0);
        if (cyanBlend > 0.15) {
          ctx.strokeStyle = `rgba(180,224,232,${baseA * (1 + cyanBlend * 0.25)})`;
        } else {
          ctx.strokeStyle = `rgba(237,234,227,${baseA})`;
        }
        ctx.lineWidth = filament.thickness * (1 + combinedBendWeight * 0.4 + coreDwell * 0.25);
        ctx.stroke();
        // Beam from filament tip to pointer — outside core only.
        if (
          ptrBendWeight > 0.32
          && visSegs >= filament.path.length - 2
          && samples.length
          && outsideCoreFactor > 0.4
        ) {
          const tip = samples[samples.length - 1];
          const beamA = Math.min(0.42, ptrBendWeight * 0.45);
          ctx.beginPath();
          ctx.moveTo(tip.x, tip.y);
          ctx.lineTo(px, py);
          ctx.strokeStyle = `rgba(190,232,238,${beamA})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
        if (visSegs >= filament.path.length - 1 && (filament.bright || connecting || coreDwell > 0.4) && aw > 0.6) {
          ctx.fillStyle = `rgba(180,224,232,${0.5 * aw + combinedBendWeight * 0.4 + coreDwell * 0.25})`;
          ctx.beginPath();
          ctx.arc(endX, endY, 1.2 + combinedBendWeight * 1.2 + coreDwell * 0.6, 0, Math.PI * 2);
          ctx.fill();
        }
        // pulses along filament
        for (let pi = 0; pi < pulses.length; pi += 1) {
          const pu = pulses[pi];
          const align = 0.5 + 0.5 * Math.cos(ang0 - pu.ang);
          if (align < 0.7) continue;
          const dt2 = t - pu.t0;
          if (dt2 < 0 || dt2 > 1.6) continue;
          const idx = Math.min(samples.length - 1, Math.floor((dt2 / 1.6) * (samples.length - 1)));
          const sp = samples[idx];
          if (!sp) continue;
          const fade = (1 - dt2 / 1.6) * (align ** 4) * pu.life;
          const grad = ctx.createRadialGradient(sp.x, sp.y, 0, sp.x, sp.y, 10);
          grad.addColorStop(0, `rgba(190,232,238,${0.9 * fade})`);
          grad.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, 10, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      // anchor halos
      for (let i = 0; i < anchors.length; i += 1) {
        const a = anchors[i];
        const age = t - a.birth;
        const aPulse = 0.5 + 0.5 * Math.sin(age * 2.4);
        const r = (8 + aPulse * 6) * a.life;
        const grad = ctx.createRadialGradient(a.x, a.y, 0, a.x, a.y, r * 4);
        grad.addColorStop(0, `rgba(190,232,238,${0.45 * a.life})`);
        grad.addColorStop(0.4, `rgba(180,224,232,${0.18 * a.life})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(a.x, a.y, r * 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(210,240,245,${0.7 * a.life})`;
        ctx.beginPath();
        ctx.arc(a.x, a.y, 1.4 + aPulse * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
      void w; void h;
    }

    function drawBurstGlyphs(dt) {
      const bg = burstGlyphsRef.current;
      if (!bg.length) return;
      ctx.font = '11px "JetBrains Mono", "IBM Plex Mono", monospace';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      const t = tRef.current;
      for (let i = bg.length - 1; i >= 0; i -= 1) {
        const g = bg[i];
        const age = t - g.born;
        if (age > 1.8) { bg.splice(i, 1); continue; }
        g.x += g.vx * dt;
        g.y += g.vy * dt;
        // Stronger drag so glyphs settle quickly rather than fly across.
        g.vx *= 0.90;
        g.vy *= 0.90;
        const fade = 1 - age / 1.8;
        ctx.fillStyle = `rgba(180,224,232,${fade * 0.55})`;
        ctx.fillText(g.glyph, g.x, g.y);
      }
      ctx.textAlign = 'start';
      ctx.textBaseline = 'top';
      const pulses = pulsesRef.current;
      for (let i = pulses.length - 1; i >= 0; i -= 1) {
        if (t - pulses[i].t0 > 1.7) pulses.splice(i, 1);
      }
    }

    function drawRipples(dt) {
      const arr = ripplesRef.current;
      for (let i = arr.length - 1; i >= 0; i -= 1) {
        const r = arr[i];
        // Slower expansion + slower fade-out — feels like a touch, not a blast.
        r.life += dt / 1.9;
        r.r = r.life * 220;
        if (r.life >= 1) { arr.splice(i, 1); continue; }
        const a = (1 - r.life) * 0.28 * r.strength;
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(237,234,227,${a})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // ────────── main loop ──────────
    const ease = (s, factor) => { s.current += (s.target - s.current) * factor; };
    const CORE_RADIUS = 56;       // px — "inside the entity" threshold
    const CORE_FADE_RADIUS = 96;  // px — soft outer band of the core
    let lastNow = performance.now();
    const render = (now) => {
      const dt = Math.min(0.05, (now - lastNow) / 1000);
      lastNow = now;
      tRef.current += dt;
      ease(stageIntensityRef.current, Math.min(1, dt * 1.4));
      // Slower voice easing so speaking feels like inhale/exhale, not a spike.
      // Listening/sleeping/generating fall back to a slightly faster ease.
      const viNow = voiceIntensityRef.current.current;
      const viTarget = voiceIntensityRef.current.target;
      const speakingEase = viTarget >= 0.9 ? 1.6 : 2.4;
      ease(voiceIntensityRef.current, Math.min(1, dt * speakingEase));

      // Update core-dwell intensity based on pointer distance from entity centre.
      const { w, h, cx, cy } = sizeRef.current;
      const ptr = pointerRef.current;
      const ppx = ptr.x * w;
      const ppy = ptr.y * h;
      const pdEntity = Math.hypot(ppx - cx, ppy - cy);
      let coreTarget = 0;
      if (ptr.present && stageIntensityRef.current.current > 0.4) {
        if (pdEntity < CORE_RADIUS) coreTarget = 1;
        else if (pdEntity < CORE_FADE_RADIUS) {
          coreTarget = 1 - (pdEntity - CORE_RADIUS) / (CORE_FADE_RADIUS - CORE_RADIUS);
        }
      }
      // Build-up while inside, decay when outside — both smooth.
      const growRate = coreTarget > coreDwellRef.current ? 0.55 : 1.6;
      coreDwellRef.current += (coreTarget - coreDwellRef.current) * Math.min(1, dt * growRate);

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);

      updateAnchors(dt);
      drawCodeRain(dt);
      drawCursorWave();
      // Skip the entity body in gallery mode — code rain + cursor wave +
      // click ripples + burst glyphs still provide the living background.
      if (modeRef.current !== 'gallery') drawEntity();
      drawBurstGlyphs(dt);
      drawRipples(dt);

      // silence unused
      void viNow;

      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    // ────────── teardown ──────────
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mousedown', onDown);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="ambient-canvas"
      className="fixed inset-0 z-0"
      style={{ width: '100vw', height: '100vh', display: 'block', background: '#000' }}
    />
  );
}
